import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {ActivatedRoute, NavigationCancel, NavigationEnd, NavigationStart, Router} from '@angular/router';
import { Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {AuthorizeService} from 'src/api-authorization/authorize.service';
import {MetaDataService} from 'src/app/shared/core/services/meta-data.service';
import {Environment} from './shared/core/models/environment.model';
import {SiteDataService} from './shared/core/services/site-data.service';
import { environment } from 'src/environments/environment';

declare let global: any;

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
declare let gtag: Function;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    private siteDataService = inject(SiteDataService);
    authorizeService = inject(AuthorizeService);
    private router = inject(Router);
    private meta = inject(Meta);
    private route = inject(ActivatedRoute);
    private titleService = inject(Title);
    private metaDataService = inject(MetaDataService);

    isProduction = environment.production;
    isUserLoggedIn = false;
    isLeftNavCollapsed = false;
    isAdmin = false;
    loading = false;
    hideHeader = false;

    environment: Environment;
    Environments = Environment;
    private unsubscribe$ = new Subject<void>();

    googleAnalyticsId: string;
    metaDescription: string;
    clientCss: string;
    inactivityTimeout: string;
    showInactiveTimeout: boolean;
    translationDictionary = {};
    translationUrls = {};
    translationDictionarySegments = {};
    translationFuseSearch: any;
    languagePreference?: string;
    domObserver: MutationObserver;
    isSpanishClientOptionEnabled: boolean = false;
    budgetAnalysisOptions: any;
    budgetAnalysisData: any;


    constructor() {

    }

    handleDOMMutation(mutationsList: MutationRecord[], observer: MutationObserver) {
        for (const mutation of mutationsList) {
            let isDomAddedOrRemoved = mutation.type === 'childList';
            if (isDomAddedOrRemoved) {
                mutation.addedNodes.forEach((addedNode) => {
                    if (addedNode instanceof Element && addedNode.classList.contains('p-tooltip')) {
                    }
                });
            }
        }
    }

    ngOnInit(): void {

        this.siteDataService.select(c => {
            return {
                showInactiveTimeout: c.showInactiveTimeout
            };
        })
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((data) => {
                this.showInactiveTimeout = data.showInactiveTimeout;
            });
        this.siteDataService.select(c => {
            return {
                environment: c.environmentConfig?.environmentID,
                isUserLoggedIn: c.isUserLoggedIn
            };
        })
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((data) => {
                this.isUserLoggedIn = data.isUserLoggedIn;
                this.environment = data.environment;
                if (this.environment > 0 && this.environment !== Environment.PROD) {
                    this.meta.addTag({name: 'robots', content: 'noindex, nofollow'});
                }
            });
        this.siteDataService.select(c => c.isAdmin)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(data => {
                this.isAdmin = data;
            });
        this.siteDataService.select(c => c.loading)
            .subscribe((loading) => {
                setTimeout(
                    () => {
                        this.loading = loading;
                    },
                    !loading ? 500 : 0
                );
            });
        this.siteDataService.inactivityTimeoutDisplay
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(timeout => {
                this.inactivityTimeout = timeout;
            });
        this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.clearMenuPopups();

                // Only show loader if the navigation is not just a fragment change
                if (event.restoredState && event.navigationTrigger === 'popstate') {
                    // This is a browser back/forward navigation, may include fragment
                    return;
                }
                if (event.url.startsWith(this.router.url.split('#')[0] + '#')) {
                    // This is a fragment-only navigation, skip loader
                    return;
                }

                let child = this.route.firstChild;
                let disableSiteLoader = false;
                while (child) {

                    if (child.snapshot.data && child.snapshot.data['disableSiteLoader']) {
                        disableSiteLoader = true;
                    }

                    if (child.firstChild) {
                        child = child.firstChild;
                    } else {
                        child = null;
                    }
                }

            } else if (event instanceof NavigationEnd) {
                let child = this.route.firstChild;
                let bodyClass = '';
                // this.isAdmin = false;
                // this.siteDataService.setIsAdmin(false);
                let metaTitle = '';
                let adminMetaFound = false;
                let hideHeaderMetaDataFound = false;
                while (child) {
                    if (child.snapshot.data && child.snapshot.data['bodyClass']) {
                        const currentBodyClassList = bodyClass.split(' ');
                        if (!currentBodyClassList.some((b) => b === child.snapshot.data['bodyClass'])) {
                            bodyClass += child.snapshot.data['bodyClass'] + ' ';
                        }
                    }

                    if (child.snapshot.data && child.snapshot.data.admin) {
                        adminMetaFound = true;
                        // this.siteDataService.setIsAdmin(true);
                    }
                    if (child.snapshot.data && child.snapshot.data.hideHeader) {
                        hideHeaderMetaDataFound = true;
                    }

                    // Apply page titles
                    if (child.snapshot.data && child.snapshot.data['metaTitle']) {
                        metaTitle = child.snapshot.data['metaTitle'];

                        this.titleService.setTitle(metaTitle);
                    } else {
                        this.titleService.setTitle(metaTitle);
                    }

                    if (child.firstChild) {
                        child = child.firstChild;
                    } else {
                        child = null;
                    }
                }

                this.siteDataService.setIsAdmin(adminMetaFound);
                this.hideHeader = hideHeaderMetaDataFound;
                this.siteDataService.resetInactiveTime();
                if (bodyClass) {
                    document.body.className = bodyClass;
                } else {
                    document.body.className = '';
                }

                if (this.googleAnalyticsId) {
                    gtag('config', this.googleAnalyticsId, {
                        page_path: event.urlAfterRedirects,
                    });
                }

                if (this.metaDescription) {
                    this.metaDataService.setMetaDescription(this.metaDescription);
                }
            } else if (event instanceof NavigationCancel) {
                // this.siteDataService.navigationCancelled();
            }
        });

        if ((navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) && !navigator.userAgent.includes('Mobile')) {
            document.body.classList.add('mobile');
        }
    }

    /**
     * TODO: Remove this when https://github.com/primefaces/primeng/issues/19213 is fixed
     */
    private clearMenuPopups(): void {
      const menuElements = document.querySelectorAll('body > [name="p-anchored-overlay"]');
      menuElements.forEach((node) => {
        node.remove();
      });
    }

    logout(): void {
        this.router.navigate(['/account/logout'], {relativeTo: this.route, state: {local: true}});
    }

    resetInactivity(): void {
        this.siteDataService.resetInactiveTime();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
        this.domObserver?.disconnect();
    }

    toggleLeftNav(e: boolean): void {
        this.isLeftNavCollapsed = e;
    }

}
